/**
 * Campaign report page — overall counts + per-question aggregates +
 * Excel export buttons. Backed by GET /v1/campaigns/<id>/report.
 *
 * Categorical questions get a bar list (value / count / percent).
 * Numeric questions get a min / max / avg / count card. Text-shaped
 * questions just show the answered count.
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { AppLayout } from "@/components/AppLayout";
import { BackLink } from "@/components/BackLink";
import { PermissionGate } from "@/components/PermissionGate";
import { ApiPaths, api } from "@/lib/api";
import type {
  CampaignReport,
  CampaignReportQuestion,
} from "@/lib/types";


function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}


function safeCampaignName(name: string, fallback: string): string {
  const cleaned = name.replace(/[^a-z0-9 _-]/gi, "").slice(0, 40).trim();
  return cleaned || fallback;
}


export default function CampaignReportPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const report = useQuery({
    queryKey: ["campaign-report", id],
    queryFn: async () => {
      const res = await api.get<CampaignReport>(ApiPaths.campaignReport(id!));
      return res.data;
    },
    enabled: !!id,
  });

  // The xlsx endpoint must be hit through the api client so it lands
  // on `api.nexbasira.com` with the bearer token. The previous
  // `<a href={ApiPaths.campaignReportXlsx(id!)}>` resolved against the
  // SPA origin and shipped without auth — the request 404'd or got
  // bounced by the SPA's history fallback.
  const downloadXlsx = useMutation({
    mutationFn: async () => {
      const res = await api.get<Blob>(ApiPaths.campaignReportXlsx(id!), {
        responseType: "blob",
      });
      const fallback = id ? id.slice(0, 8) : "campaign";
      const name = safeCampaignName(
        report.data?.campaign_name ?? "",
        fallback,
      );
      downloadBlob(res.data, `nexbasira-report-${name}.xlsx`);
    },
  });

  return (
    <AppLayout
      eyebrow={t("report.eyebrow", "Campaign")}
      title={
        report.data?.campaign_name || t("report.title", "Campaign report")
      }
    >
      <BackLink
        fallback={`/campaigns/${id}`}
        className="text-sm text-nb-text-2 hover:underline"
      >
        ← {t("common.back", "Back")}
      </BackLink>

      <PermissionGate
        perm="campaign.read"
        fallback={
          <p className="card text-sm text-nb-text-2">
            {t("admin.noPermission", "You don't have permission")}
          </p>
        }
      >
        {report.isLoading && (
          <p className="card text-sm text-nb-text-3">{t("common.loading")}</p>
        )}
        {report.isError && (
          <p className="card text-sm text-cvp-danger">
            {t("report.loadError", "Could not load this report.")}
          </p>
        )}
        {report.data && (
          <>
            <header className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-wrap gap-3">
                <SummaryCard
                  label={t("report.memberships", "Beneficiaries")}
                  value={report.data.totals.memberships}
                />
                <SummaryCard
                  label={t("report.sessionsTotal", "Sessions")}
                  value={report.data.totals.sessions_total}
                />
                <SummaryCard
                  label={t("report.sessionsDone", "Done")}
                  value={report.data.totals.sessions_done}
                />
                <SummaryCard
                  label={t("report.sessionsOpen", "Open")}
                  value={report.data.totals.sessions_open}
                />
                {(report.data.totals.sessions_conform > 0 ||
                  report.data.totals.sessions_non_conform > 0 ||
                  report.data.totals.sessions_conformity_pending > 0) && (
                  <>
                    <SummaryCard
                      label={t("report.sessionsConform", "Conform")}
                      value={report.data.totals.sessions_conform}
                      accent="success"
                    />
                    <SummaryCard
                      label={t("report.sessionsNonConform", "Non-conform")}
                      value={report.data.totals.sessions_non_conform}
                      accent="danger"
                    />
                    <SummaryCard
                      label={t(
                        "report.sessionsConformityPending",
                        "Pending conformity",
                      )}
                      value={report.data.totals.sessions_conformity_pending}
                      accent="neutral"
                    />
                  </>
                )}
              </div>
              <button
                type="button"
                className="btn-primary text-sm"
                onClick={() => downloadXlsx.mutate()}
                disabled={downloadXlsx.isPending}
              >
                {downloadXlsx.isPending
                  ? t("report.downloadingXlsx", "Preparing…")
                  : t("report.downloadXlsx", "Download report (.xlsx)")}
              </button>
            </header>

            {report.data.totals.memberships_by_status && (
              <section className="card space-y-2">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-nb-text-2">
                  {t("report.byStatus", "Memberships by status")}
                </h2>
                <ul className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs sm:grid-cols-4">
                  {Object.entries(
                    report.data.totals.memberships_by_status,
                  ).map(([status, count]) => (
                    <li
                      key={status}
                      className="flex items-center justify-between gap-2 border-b border-nb-border/40 py-1"
                    >
                      <span className="text-nb-text-2">{status}</span>
                      <strong className="text-nb-text">{count}</strong>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            <section className="space-y-3">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-nb-text-2">
                {t("report.questions", "Questions")}
              </h2>
              {report.data.questions.length === 0 && (
                <p className="card text-xs text-nb-text-3">
                  {t(
                    "report.questionsEmpty",
                    "No questions configured for this campaign.",
                  )}
                </p>
              )}
              {report.data.questions.map((q) => (
                <QuestionCard key={q.question_id} question={q} />
              ))}
            </section>
          </>
        )}
      </PermissionGate>
    </AppLayout>
  );
}


function SummaryCard({
  label,
  value,
  accent,
}: {
  label: string;
  value: number;
  /** Optional tone for conformity cards — mirrors the pass/fail
   *  language used elsewhere (signatures, quota banners). */
  accent?: "success" | "danger" | "neutral";
}) {
  const valueClass =
    accent === "success"
      ? "text-cvp-ok"
      : accent === "danger"
        ? "text-cvp-danger"
        : "text-nb-text";
  return (
    <div className="card flex min-w-[8rem] flex-col gap-0.5 py-2">
      <span className="text-[10px] uppercase tracking-wide text-nb-text-3">
        {label}
      </span>
      <span className={`text-xl font-semibold ${valueClass}`}>{value}</span>
    </div>
  );
}


function QuestionCard({ question }: { question: CampaignReportQuestion }) {
  const { t } = useTranslation();
  return (
    <div className="card space-y-2">
      <header className="flex items-baseline justify-between gap-2">
        <h3 className="text-sm font-medium text-nb-text">
          <span className="mr-2 font-mono text-[10px] text-nb-text-3">
            #{question.order}
          </span>
          {question.label}
        </h3>
        <span className="flex items-center gap-2 text-[10px] uppercase text-nb-text-3">
          <span className="rounded bg-nb-surface px-1.5 py-0.5">
            {question.kind}
          </span>
          <span>
            {question.answered_count}{" "}
            {t("report.answered", "answered")}
          </span>
        </span>
      </header>

      {question.breakdown && Object.keys(question.breakdown).length > 0 && (
        <BreakdownBars
          breakdown={question.breakdown}
          options={question.options}
        />
      )}

      {question.numeric && (
        <div className="grid grid-cols-3 gap-2 text-xs">
          <div className="rounded bg-nb-surface/40 px-2 py-1">
            <span className="block text-[10px] text-nb-text-3">
              {t("report.min", "Min")}
            </span>
            <strong>{question.numeric.min ?? "—"}</strong>
          </div>
          <div className="rounded bg-nb-surface/40 px-2 py-1">
            <span className="block text-[10px] text-nb-text-3">
              {t("report.avg", "Avg")}
            </span>
            <strong>
              {question.numeric.avg !== null
                ? question.numeric.avg.toFixed(2)
                : "—"}
            </strong>
          </div>
          <div className="rounded bg-nb-surface/40 px-2 py-1">
            <span className="block text-[10px] text-nb-text-3">
              {t("report.max", "Max")}
            </span>
            <strong>{question.numeric.max ?? "—"}</strong>
          </div>
        </div>
      )}
    </div>
  );
}


function BreakdownBars({
  breakdown,
  options,
}: {
  breakdown: Record<string, number>;
  options: Array<{ value: string; label: string }>;
}) {
  const total = Object.values(breakdown).reduce((a, b) => a + b, 0) || 1;
  const labelByValue = new Map(options.map((o) => [o.value, o.label]));
  const rows = Object.entries(breakdown).sort((a, b) => b[1] - a[1]);
  return (
    <ul className="space-y-1 text-xs">
      {rows.map(([value, count]) => {
        const pct = (count * 100) / total;
        return (
          <li key={value}>
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-nb-text">
                {labelByValue.get(value) ?? value}
                <span className="ml-1 text-nb-text-3">({value})</span>
              </span>
              <span className="text-nb-text-2">
                {count} · {pct.toFixed(1)}%
              </span>
            </div>
            <div className="h-1.5 rounded bg-nb-surface">
              <div
                className="h-1.5 rounded bg-nb-mint"
                style={{ width: `${pct}%` }}
              />
            </div>
          </li>
        );
      })}
    </ul>
  );
}
